import { Plus, XIcon } from 'lucide-react';
import { Fragment } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { AddUserDocument } from 'src/graphql/actions/user/addUser.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { refetchUsers } from 'src/containers/Application/Memberships/Management/api';

import { useInvite, type IMemberInvite } from '../model';

const CardMember = ({ id, name, email, role, onRemove }: IMemberInvite & { onRemove: (id: string) => void }) => {
  const avatarUrl = getGravatarUrl(email);

  const handleRemove = () => onRemove(id);

  return (
    <Card className="py-2.5 px-3">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-2 relative">
        <Avatar className="h-8.5 w-8.5 rounded-md">
          <AvatarImage src={avatarUrl} alt="avatar" />
        </Avatar>
        <div className="flex-1 flex flex-col min-w-0">
          <h3 className="text-xs font-semibold truncate">{name}</h3>
          <span className="text-xs text-muted-foreground capitalize">{role}</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleRemove}>
          <XIcon className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
};

const InviteMembers = () => {
  const { members, createInvite, removeMember, setInitial } = useInvite();
  const [addMember, { loading }] = useMutation(AddUserDocument);
  const navigate = useNavigate();

  const onSendInvite = async () => {
    const users = members.map(({ id, ...rest }) => ({ ...rest }));

    try {
      const { data } = await addMember({ variables: { users } });

      if (data?.addUser?.success) {
        setInitial();
        refetchUsers();
        return navigate('/');
      }
      throw new ClientError(data?.addUser?.errors?.[0]);
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Fragment>
      <div className="space-y-2 mb-4 max-h-75 overflow-y-auto">
        {members.map((member) => (
          <CardMember key={member.id} {...member} onRemove={removeMember} />
        ))}
      </div>
      <Button variant="outline" size="lg" className="w-full mb-2" onClick={createInvite}>
        <Plus />
        Create another invitation
      </Button>
      <Button
        size="lg"
        className="w-full"
        onClick={onSendInvite}
        disabled={!members.length || loading}
        isLoading={loading}
      >
        Send invitations and continue
      </Button>
    </Fragment>
  );
};

export default InviteMembers;
